import React from 'react';
import { Tooltip } from 'antd';
import moment from 'moment';

import Typography from '../../../components/Typography';
import COLORS from '../../../util/constants/colors';
import {
  formatDOB,
  formatPhoneNumber,
  formatName,
  formatDuration,
} from '../helpers';
import {
  getDisconnectionReasonTag,
  getScheduledViaMairaTag,
  getResultDeliveryTag,
  renderStatusPill,
} from './constants';

const findDepartmentName = (departmentId, departments) => {
  if (!departmentId) return null;
  const match = (departments || []).find((dept) => dept.slug === departmentId);
  return match?.name || 'Unknown department';
};

const departmentColumn = (departments) => ({
  title: 'Department',
  dataIndex: 'department_id',
  key: 'department_id',
  width: 190,
  render: (departmentId) => {
    const name = findDepartmentName(departmentId, departments);
    return (
      <Typography
        variant="text"
        size="small"
        color={name ? COLORS.TEXT_PRIMARY_LITE : COLORS.TEXT_TERTIARY_LITE}
      >
        {name || '--'}
      </Typography>
    );
  },
});

const dateColumn = ({ field, label }) => {
  const dataIndex = field;
  return {
    title: label,
    dataIndex,
    key: dataIndex,
    width: 160,
    render: (text) => {
      if (!text) {
        return (
          <Typography
            variant="text"
            size="small"
            color={COLORS.TEXT_TERTIARY_LITE}
          >
            --
          </Typography>
        );
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography
            variant="text"
            size="small"
            color={COLORS.TEXT_PRIMARY_LITE}
            bold
          >
            {moment(text).format('MM/DD/YYYY')}
          </Typography>
          <Typography
            variant="text"
            size="small"
            color={COLORS.TEXT_TERTIARY_LITE}
          >
            {moment(text).format('hh:mm A')}
          </Typography>
        </div>
      );
    },
  };
};

// Status column. One pill per row: with the maira_scheduling_status flag on, a
// call MAIRA actually scheduled (scheduling_outcome === 'scheduled') shows the
// green "Scheduled via MAIRA" pill INSTEAD of its disconnection-reason pill.
// Every other call -- including scheduling_outcome === 'not_scheduled' -- keeps
// its disconnection pill exactly as before.
const statusColumn = ({ schedulingStatusEnabled = false } = {}) => ({
  title: 'Status',
  dataIndex: 'disconnection_reason',
  key: 'disconnection_reason',
  width: 160,
  render: (_text, record) => {
    if (schedulingStatusEnabled) {
      const scheduledTag = getScheduledViaMairaTag(record);
      if (scheduledTag) {
        return renderStatusPill(scheduledTag);
      }
    }

    const reason = getDisconnectionReasonTag(record);
    if (!reason) {
      return <Typography variant="text" size="small">--</Typography>;
    }

    return renderStatusPill(reason);
  },
});

// ENG-3107: inbound projection of the ENG-3061 delivery lifecycle. Rendered as
// its own column rather than folded into Status, because a call can both end in
// a hangup AND have delivered a result -- collapsing them would hide one fact
// behind the other. Absent `result_delivery` (no delivery, or an org without the
// inbound_result_delivery_outcomes feature) renders the same "--" as any other
// empty cell, so the column is inert rather than alarming.
// A plain object like the baseInboundColumns entries — it takes no options, unlike
// departmentColumn(departments) and statusColumn({...}), which earn their factories.
const resultDeliveryColumn = {
  title: 'Results Delivered',
  dataIndex: 'result_delivery',
  key: 'result_delivery',
  width: 180,
  render: (_value, record) => {
    const tag = getResultDeliveryTag(record);
    if (!tag) {
      return <Typography variant="text" size="small">--</Typography>;
    }

    return renderStatusPill(tag);
  },
};

const baseInboundColumns = [
  {
    title: 'Caller Name',
    dataIndex: 'caller_name',
    key: 'caller_name',
    width: 220,
    render: (name, record) => {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography
            variant="text"
            size="small"
            bold
          >
            {formatName(name) || 'Not Provided'}
          </Typography>
          <Typography
            variant="text"
            size="xsmall"
            color={COLORS.TEXT_TERTIARY_LITE}
          >
            {record.caller_dob && record.caller_dob !== 'Not mentioned' ? formatDOB(record.caller_dob) : '--/--/----'}
          </Typography>
        </div>
      );
    },
    fixed: 'left',
  },
  {
    title: 'Phone Number',
    dataIndex: 'caller_number',
    key: 'caller_number',
    width: 150,
    render: (text) => (
      <Typography
        variant="text"
        size="small"
        color={COLORS.TEXT_SECONDARY_LITE}
      >
        {formatPhoneNumber(text)}
      </Typography>
    ),
  },
  dateColumn({ field: 'created_at', label: 'Date and Time' }),
  {
    title: 'Duration',
    dataIndex: 'duration_seconds',
    key: 'duration_seconds',
    width: 130,
    render: (seconds) => {
      return (
        <Typography
          variant="text"
          size="small"
          color={COLORS.TEXT_PRIMARY_LITE}
          bold
        >
          {formatDuration(seconds)}
        </Typography>
      );
    },
  },
  statusColumn(),
  {
    title: 'Summary',
    dataIndex: 'call_summary',
    key: 'call_summary',
    width: 380,
    ellipsis: true,
    render: (text) => {
      if (!text || text === '[]' || text === '""') {
        return '-';
      }
      return (
        <Tooltip
          title={text}
          placement="bottomLeft"
        >
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: COLORS.TEXT_TERTIARY_LITE,
            }}
          >
            {text}
          </div>
        </Tooltip>
      );
    },
  },
];

// Insert `column` immediately before/after the column with `anchorKey`, falling back
// to the end of the list if the anchor is missing. Shared by the optional columns so
// each one states its own position and none depends on another having run first.
const insertRelativeTo = (columns, anchorKey, placement, column) => {
  const anchorIndex = columns.findIndex((col) => col.key === anchorKey);
  const insertAt = anchorIndex === -1
    ? columns.length
    : anchorIndex + (placement === 'after' ? 1 : 0);

  return [...columns.slice(0, insertAt), column, ...columns.slice(insertAt)];
};

const buildInboundColumns = ({
  showDepartment = false,
  departments = [],
  schedulingStatusEnabled = false,
  showResultDelivery = false,
  timestamp = { field: 'created_at', label: 'Date and Time' },
} = {}) => {
  let columns = baseInboundColumns;

  if (timestamp.field !== 'created_at' || timestamp.label !== 'Date and Time') {
    columns = columns.map((col) => (col.key === 'created_at' ? dateColumn(timestamp) : col));
  }

  if (schedulingStatusEnabled) {
    // Swap in the scheduling-aware Status cell (scheduled calls show the green
    // "Scheduled via MAIRA" pill instead of their disconnection pill).
    columns = columns.map((col) => (
      col.key === 'disconnection_reason' ? statusColumn({ schedulingStatusEnabled: true }) : col
    ));
  }

  // Both optional columns are positioned relative to Status, so each insert is
  // expressed against it explicitly rather than against whatever the previous
  // insert left behind. insertRelativeTo re-finds the anchor every time, so the
  // two blocks are order-independent and can be read in isolation.
  // ENG-3107: result delivery sits immediately AFTER Status so the call's own
  // outcome and what the call accomplished for the patient read together.
  if (showResultDelivery) {
    columns = insertRelativeTo(columns, 'disconnection_reason', 'after', resultDeliveryColumn);
  }

  // ENG-2768: Department sits immediately BEFORE Status.
  if (showDepartment) {
    columns = insertRelativeTo(columns, 'disconnection_reason', 'before', departmentColumn(departments));
  }

  return columns;
};

export default buildInboundColumns;
export {
  buildInboundColumns,
  baseInboundColumns,
  findDepartmentName,
};
